import {
  ProgrammteilnahmeEmailFieldBehaviour,
  UebergabeVertragsschlussverfahren,
  VermittlerFom,
  Verwendungszweck,
  Zahlungsart,
  Zahlweise,
} from '@ps20/bff-models';
import { ConfirmationState, PersonensucheModal } from '@ps20-frontend/frontend-models';
import dayjs from 'dayjs';
import { DATEFORMAT_LOCAL_STATE } from '@ps20-frontend/utils';
import { DruckstatusEnum } from './reducer/druckstatus.reducer';
import { IAppState } from './localStore';

export const INITIAL_STATE: IAppState = {
  version: 29,
  role: null,
  kdv: {
    vorId: null,
    sessionId: null,
    perId: null,
    vermerk: null,
    gesamtbeitrag: null,
    azamVorgangsId: null,
    azamCryptId: null,
    partyId: null,
  },
  beratung: {
    beratername: '',
    beratungsdatum: dayjs().format(DATEFORMAT_LOCAL_STATE),
    besonderheitenBesprochen: null,
    besonderheiten: '',
    extremwetterschutzBesonderheiten: '',
    verkaufInnerhalbZielmarkt: null,
    assekuranzTarifChecked: false,
    assekuranzPersonenAngaben: [],
  },
  vorversicherungen: [],
  versicherungData: [],
  produkte: [],
  vermittler: {
    vorname: '',
    nachname: '',
    telefon: '',
    selectedVermittlernummer: {
      fremd: {
        vertreternummer: '',
        geschaeftsstelle: '',
        zweigniederlassung: '',
        vermittlerFom: VermittlerFom.NONE,
      },
      eigen: {
        vertreternummer: '',
        geschaeftsstelle: '',
        zweigniederlassung: '',
        vermittlerFom: VermittlerFom.NONE,
      },
    },
    bnrb: '',
    bnrbs: [],
    bsvtnrs: [],
    vermittlernummern: [],
    fremd: false,
    fom: '',
    isInkasso: false,
  },
  versicherungsnehmer: {
    kundenverwaltungID: null,
    kundennummer: null,
    anrede: null,
    vorname: '',
    nachname: '',
    firmenname: '',
    juristisch: false,
    businessForm: {
      wert: '',
      text: '',
    },
    geburtsdatum: '',
    isAssekuranztarif: false,
    staatsbschaft: undefined,
    plz: '',
    plzValid: true,
    ort: '',
    strasse: '',
    hausnummer: '',
    email: {
      vorbelegt: false,
      wert: '',
      kommunikationsArt: undefined,
      verwendung: [],
      herkunft: null,
      angabeStatus: null,
      fieldBehaviour: ProgrammteilnahmeEmailFieldBehaviour.OFFEN,
      changedByUser: false,
    },
    mobil: {
      vorbelegt: false,
      wert: '',
      kommunikationsArt: undefined,
      verwendung: [],
      herkunft: null,
      angabeStatus: null,
    },
    telefon: {
      vorbelegt: false,
      wert: '',
      kommunikationsArt: undefined,
      verwendung: [],
      herkunft: null,
      angabeStatus: null,
    },
    fax: {
      vorbelegt: false,
      wert: '',
      kommunikationsArt: undefined,
      verwendung: [],
      herkunft: null,
      angabeStatus: null,
    },
    vnVorbelegt: false,
    bankverbindungen: null,
    adressvorschlag: {
      orte: [],
      strassen: [],
      lastError: false,
    },
  },
  sicherungsglaeubiger: [],
  zahlung: {
    zahlungsart: Zahlungsart.LASTSCHRIFT,
    zahlweise: Zahlweise.MONATLICH,
    iban: '',
    bic: '',
    bankname: '',
    bankort: '',
    sepaErteilt: false,
    vorbelegt: false,
    manuelleIbanEingabe: false,
    verwendungszweck: Verwendungszweck.UNBEKANNT,
    showValidationError: undefined,
    kontoinhaber: null,
    azam: {
      abweichenderAzamKontoinhaber: null,
      staatsangehoerigkeiten: [],
      adressvorschlag: {
        lastError: null,
        orte: [],
        strassen: [],
      },
    },
  },
  vertragsschlussverfahren: null,
  effectiveZaehlstuecke: 0,
  unfallZaehlstuecke: 0,
  telefonverkauf: {
    fandMitVNStatt: true,
    vornameGespraechspartner: '',
    nachnameGespraechspartner: '',
    datumGespraech: dayjs().format(DATEFORMAT_LOCAL_STATE),
    uhrzeitGespraech: '',
    versandArtAVB: UebergabeVertragsschlussverfahren.DRUCK_POST,
    emailAVBVersand: '',
  },
  antragResponse: null,
  sammelantragsnummer: null,
  druckstatus: {
    beiblattStatus: DruckstatusEnum.NICHT_GEDRUCKT,
    infopaketStatus: DruckstatusEnum.NICHT_GEDRUCKT,
    pkbStatus: DruckstatusEnum.NICHT_GEDRUCKT,
    jagdbestaetigungStatus: DruckstatusEnum.NICHT_GEDRUCKT,
    weweStatus: DruckstatusEnum.NICHT_GEDRUCKT,
  },
  transientState: {
    fileModificationInProgress: false,
    vorbelegungIsInProgress: false,
    amisCRMRquestInProgress: false,
    requestFailed: false,
    tarifierungIsInProgress: false,
    einreichenButtonClicked: false,
    infoPaketDruckIsInProgress: false,
    beiblattDruckIsInProgress: false,
    pkbDruckIsInProgress: false,
    jagdbestaetigungDruckIsInProgress: false,
    weweDruckIsInProgress: false,
    einreichenIsInProgress: false,
    testdokumenteDownloadIsInProgress: false,
    featureToggles: {},
    regexps: {},
    role: null,
    accessToken: null,
    showRabattHinweis: false,
    personenSuchdialog: {
      searchString: '',
      items: [],
      open: false,
      error: false,
      errorMessage: '',
      loading: false,
      selectedItem: null,
      confirmed: false,
      renderedColumns: ['icon', 'name', 'geburtsdatum', 'ort', 'strasse', 'status'],
      confirmation: ConfirmationState.NOT_STARTED,
      personensucheModal: PersonensucheModal.CLOSED,
    },
    onboardingVideo: false,
    zurueckZumShopModalOpen: false,
    immoModalOpened: false,
    validationTriggered: false,
    fileDeletion: [],
    fragebogenValidationRevealed: [],
    elementarfragebogenLastSuccessfullOperation: [],
  },
  immobilien: [],
  vermieteteWohnungen: [],
  maklermandatVorhanden: null,
  thhPferdAngaben: {
    pferderassenListe: [],
    thhPferdAngebotVorhanden: false,
    tiere: [],
    angabenListe: [],
  },
  thhHundAngaben: {
    thhHundAngebotVorhanden: false,
    angabenListe: [],
    tiere: [],
  },
  jhpAngaben: {
    jagdschein: null,
    jagdscheinAblaufdatum: '',
    jagdBehoerde: {
      behoerdename: '',
      ort: null,
      strasse: null,
      hausnummer: null,
      plz: null,
      adressvorschlag: {
        lastError: null,
        strassen: [],
        orte: [],
      },
    },
  },
  meldebehoerde: {
    isMeldebehoerdeVorhanden: false,
    behoerdename: null,
    isAktenzeichenUnbekannt: false,
    aktenzeichen: null,
    ort: null,
    strasse: null,
    hausnummer: null,
    plz: null,
    adressvorschlag: {
      lastError: null,
      strassen: [],
      orte: [],
    },
    beginnSicherungsbestaetigung: dayjs().add(1, 'd').format(DATEFORMAT_LOCAL_STATE),
  },
  tkvAngaben: {
    angabenListe: [],
  },
  tkvAngabenV2: {
    angabenItems: [],
    healthStatusList: {
      dogCat: [],
      horse: [],
    },
    dogBreeds: [],
    horseBreeds: [],
    catBreeds: [],
  },
  vnWechselAngaben: {
    vnWechselGrundAngabenVorhanden: false,
    angabenListe: [],
    vnWechselgrundList: [],
  },
  policeVersand: {
    versandArt: null,
  },
  unfAngaben: {
    vpAngabenListe: [],
    datenschutzEinwilligung: false,
    ausschlussList: [],
    bezugsberechtigterModal: {},
  },
  mindestvertragslaufzeit: false,
  dokumentId: null,
  fernabsatz: null,
};
